<template>
	<!-- Outer layer is for being able to overwrite the card color -->
	<ScopedColorTheme
		:is="theme ? 'ScopedColorTheme' : 'div'"
		:class="['c-list-item', { 'c-list-item--filled': filled }]"
		:theme="theme"
	>
		<!-- The actual card starts from here -->
		<Component
			:is="url ? 'LinkTile' : tag"
			:id="computedId"
			:tag="tag"
			:aria-labelledby="ariaLabelledBy"
			:to="url"
		>
			<div
				class="
					>=768:flex
					flex-grow
					px-xs/h
					>=768:pl-sm/h >=768:pr-md/h
					py-2xs/v
				"
			>
				<slot name="left" v-bind="slotProps"></slot>

				<div
					class="
						flex flex-col flex-shrink flex-grow
						pb-40
						>=768:pl-md/h >=768:pb-0 >=768:pr-48
					"
				>
					<slot name="header" v-bind="slotProps">
						<div
							v-if="label || computedDate || areas"
							class="flex flex-wrap items-center gap-8"
						>
							<small
								v-if="label || computedDate"
								class="w-full >=768:w-auto pr-3xs/h"
							>
								<span
									v-if="label"
									:id="labelId"
									:class="[
										'inline-block font-medium uppercase text-label-sm',
										{ 'text-theme': !filled },
									]"
									v-text="label"
								></span>
								<br
									v-if="label && computedDate"
									class=">=768:hidden"
								/>
								<span
									v-if="computedDate"
									:class="[
										'c-list-item__date-stamp',
										'inline-block font-medium uppercase text-label-sm',
										filled
											? 'text-white'
											: 'text-gray-darker',
									]"
									v-text="computedDate"
								></span>
							</small>

							<AreaReferences
								v-if="areas"
								class="contents"
								:items="areas"
							/>
						</div>
					</slot>

					<div class="my-24">
						<div class="flex items-start">
							<slot name="content" v-bind="slotProps">
								<div class="w-6/12col flex-grow flex flex-col">
									<div
										v-if="computedName"
										class="relative w-full"
										:lang="partLang"
									>
										<slot
											name="indicator"
											v-bind="slotProps"
										>
											<SvgIconGt
												class="
													c-list-item__link-indicator
													hidden
													>=768:block
												"
											/>
										</slot>
										<BaseH4
											v-if="computedName"
											:id="nameId"
											:level-offset="headingLevelOffset"
											class="
												c-list-item__heading
												list-title-sm
												max-w-text
											"
											v-html="computedName"
										/>
									</div>

									<slot
										name="afterHeading"
										v-bind="slotProps"
									></slot>
								</div>
							</slot>
							<div
								v-if="$scopedSlots.contentRight"
								class="
									w-fit
									min-w-min
									flex-grow
									transform
									translate-x-xs/h
								"
							>
								<slot
									name="contentRight"
									v-bind="slotProps"
								></slot>
							</div>
						</div>
						<AccordionPanel
							v-if="hasAccordionContent"
							:id="panelId"
							ref="panel"
							:transition="{
								name: 't-list-item',
							}"
							@change="({ isOpen }) => (panelIsOpen = isOpen)"
						>
							<div class="max-w-text pt-32 >=768:pt-2xs/v">
								<p
									v-if="teaser"
									class="
										font-light
										text-body-xs
										whitespace-pre-wrap
										pr-xs/h
									"
									v-html="teaser"
								></p>
								<slot
									v-if="!$scopedSlots.content"
									v-bind="slotProps"
								></slot>
							</div>
						</AccordionPanel>
						<slot
							v-if="$scopedSlots.content"
							v-bind="slotProps"
						></slot>
					</div>

					<slot name="footer" v-bind="slotProps">
						<!-- Content which will show at the bottom even when the accordion is closed -->
					</slot>
					<AccordionHeader
						v-if="hasAccordionContent"
						:id="headerId"
						class="absolute right-xs/h >=768:right-md/h bottom-xs/v"
						:class="[
							'w-40 >=768:w-48 h-40 >=768:h-48 p-0',
							'flex items-center justify-center',
							'rounded-full ',
							'transition duration-200 ease-in-out',

							{
								'border border-theme bg-theme text-white hover:bg-theme-90 transform -rotate-180':
									panelIsOpen && !filled,
								'border border-theme text-theme hover:bg-theme hover:text-white':
									!panelIsOpen && !filled,
								'border border-white bg-white text-theme hover:bg-theme-10 transform -rotate-180':
									panelIsOpen && filled,
								'border border-white text-white hover:bg-white hover:text-theme':
									!panelIsOpen && filled,
							},
						]"
						:aria-labelledby="`${nameId} ${headerId}`"
						:aria-controls="panelId"
					>
						<SvgIconWedge
							class="w-10 >=768:w-12 h-8 transform translate-y-1"
						/>
					</AccordionHeader>
				</div>
			</div>
		</Component>
	</ScopedColorTheme>
</template>

<script>
import ScopedColorTheme from '~/components/main/ScopedColorTheme';
import LinkTile from '~/components/shared/LinkTile';
import { AccordionHeader, AccordionPanel } from '~/components/shared/Accordion';
import AreaReferences from '~/components/main/AreaReferences';
import SvgIconGt from '~/assets/svgs/icon-gt.svg?inline';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';

/*
 * ListItem
 * Still needs to have date added as a prop and added to
 * the header if the value exists.
 * Also still needs to have a "tags" (or something like
 * that) prop added as well as added to the header if the
 * value/list exists.
 *
 * Other content (special image, extra info, etc.) should
 * be slotted in as to not make this component more
 * complex than needed.
 */

export default {
	name: 'ListItem',

	components: {
		ScopedColorTheme,
		LinkTile,
		AccordionHeader,
		AccordionPanel,
		AreaReferences,
		SvgIconGt,
		SvgIconWedge,
	},

	inject: {
		injectedTheme: {
			from: 'theme',
			default: () => ({ color: 'red' }),
		},
	},

	props: {
		tag: {
			type: String,
			default: 'article',
		},

		theme: {
			type: String,
			default: null,
		},

		filled: {
			type: Boolean,
			default: false,
		},

		id: {
			type: [Number, String],
			default: '',
		},

		template: {
			type: String,
			default: '',
		},

		headingLevelOffset: {
			type: [Number, String],
			default: 2,
		},

		label: {
			type: String,
			default: '',
		},

		date: {
			type: String,
			default: '',
		},

		name: {
			type: String,
			default: '',
		},

		title: {
			type: String,
			default: '',
		},

		teaser: {
			type: String,
			default: '',
		},

		url: {
			type: String,
			default: '',
		},

		image: {
			type: Object,
			default: null,
		},

		iconUrl: {
			type: String,
			default: '',
		},

		usePlaceholder: {
			type: [Boolean, Number],
			default: true,
		},

		placeholderIndex: {
			type: [Number, String],
			default: -1,
		},

		details: {
			type: Object,
			default: () => ({}),
		},

		lang: {
			type: String,
			default: null,
		},

		culture: {
			type: String,
			default: null,
		},
	},

	data() {
		const siteLang =
			{
				11050: 'en-GB',
			}[this.$store.state?.site?.cultureId] || 'da-DK';
		return {
			panelIsOpen: false,
			siteLang,
		};
	},

	computed: {
		computedId() {
			return this.id || `c-list-item-${this._uid}`;
		},

		computedTheme() {
			if (this.theme && this.theme === this.injectedTheme.color) {
				return '';
			}
			return this.theme || this.injectedTheme.color || 'red';
		},

		computedName() {
			if (this.details.apa) {
				return this.details.apa;
			}
			return this.title || this.name;
		},

		computedDate() {
			if (
				!this.template ||
				['newsPage', 'eventPage', 'publication', 'project'].includes(
					this.template
				)
			) {
				return this.date;
			}
			return '';
		},

		partLang() {
			let lang = null;
			if (this.lang) {
				const map = {
					da: 'da-DK',
					en: 'en-GP',
				};
				lang = map[this.lang] || this.lang;
			}
			if (this.culture) {
				const map = {
					da: 'da-DK',
					en: 'en-GP',
				};
				lang = map[this.culture] || this.culture;
			}
			if (lang && lang !== this.siteLang) {
				return lang;
			}
			return null;
		},

		labelId() {
			return this.label ? `${this.computedId}__label` : null;
		},

		nameId() {
			return this.name ? `${this.computedId}__title` : null;
		},

		headerId() {
			return `${this.computedId}__header`;
		},

		panelId() {
			return `${this.computedId}__panel`;
		},

		ariaLabelledBy() {
			return (
				[this.labelId, this.nameId, this.computedId]
					.filter(Boolean)
					.join(' ') || null
			);
		},

		areas() {
			if (
				!this.template ||
				['newsPage', 'eventPage', 'publication', 'project'].includes(
					this.template
				)
			) {
				return this.details?.areas?.length && this.details?.areas;
			}
			return null;
		},

		hasAccordionContent() {
			return (
				!this.$scopedSlots.content &&
				(this.$scopedSlots.default || this.teaser)
			);
		},

		slotProps() {
			return {
				id: this.computedId,
				name: this.computedName,
				teaser: this.computedTeaser,
				date: this.computedDate,
				areas: this.areas,
				hasAccordionContent: this.hasAccordionContent,
			};
		},
	},
};
</script>

<style lang="postcss">
.c-list-item--filled > * {
	@apply bg-theme text-white;

	&:before {
		@apply absolute left-0 top-0 w-full h-full;
		@apply transition-opacity duration-200 opacity-0 pointer-events-none;
		@apply bg-theme-40 bg-opacity-20;
		content: '';
		z-index: -1;
	}
	&:hover:before,
	&:focus-within:before {
		@apply opacity-100;
	}
}

.c-list-item:not(.c-list-item--filled) > * {
	@apply bg-white text-text;

	&:before,
	&:after {
		@apply absolute left-0 top-0 w-full h-full;
		@apply transition-opacity duration-200 opacity-0 pointer-events-none;
		content: '';
	}
	&:before {
		@apply bg-theme-40 bg-opacity-20;
		z-index: -1;
	}
	&:after {
		@apply border border-solid border-theme-40;
		top: -1px;
		left: -1px;
		width: calc(100% + 1.01px);
		height: calc(100% + 1.01px);
	}
	&:hover:before,
	&:focus-within:before,
	&:hover:after,
	&:focus-within:after {
		@apply opacity-100;
	}
}

.c-list-item > * {
	@apply relative isolate flex items-stretch;
}

.c-list-item__link-indicator {
	@apply absolute text-theme;
	right: calc(
		100% +
			(
				var(
						--theme-horizontalSpacing-md-h,
						var(--theme-horizontalSpacing-md-h--sm)
					) - 21px
			) / 2
	);
	width: 21px;
	height: 21px;
	margin-bottom: -2px;

	transition: opacity 0.2s, translate 0.2s ease-in-out;
	translate: -16px 0;
	opacity: 0;
}

.c-list-item__heading {
	& a[href] {
		@apply text-theme underline;

		& * {
			@apply pointer-events-none;
		}
	}
}

.c-list-item--filled {
	.c-list-item__link-indicator {
		@apply text-white;
	}

	.c-list-item__heading a[href] {
		@apply text-white;
	}

	.c-area-references__item {
		@apply bg-white;
	}
}

.c-list-item:hover .c-list-item__link-indicator,
.c-list-item:focus-within .c-list-item__link-indicator {
	translate: 0;
	opacity: 1;
}

@screen >=768 {
	.c-list-item__date-stamp:not(:first-child):before {
		content: ' — ' / '';
		opacity: 0.5;
	}
}

.c-list-item__areas {
	margin-inline: -4px;
	width: calc(100% + 8px);
}
.c-list-item__areas > :last-child {
	margin-right: 42px;
}

.t-list-item-enter-active,
.t-list-item-leave-active {
	transition: opacity 0.2s, max-height 0.2s;
}
.t-list-item-enter,
.t-list-item-leave-to {
	opacity: 0;
	max-height: 0;
}
</style>
